import type { InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, RegisterOptions, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { Checkbox } from './Checkbox';
import type { SvgIconComponent } from '@mui/icons-material';

interface DateInputProps<T extends FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  registerNameCheckbox?: Path<T>;
  isInfinite?: boolean;
  isInfineChecked?: boolean;
  label?: string;
  registerProps?: RegisterOptions<T, Path<T>>;
  isSecondary?: boolean;
  isDisabled?: boolean;
  error?: FieldValues['errors'];
  placeholder?: string;
  icon?: SvgIconComponent;
  withTime?: boolean;
  watch?: UseFormWatch<T>;
}

export const DateInput = <T extends FieldValues>({
  label,
  register,
  registerName,
  registerProps,
  isSecondary,
  isDisabled,
  error,
  placeholder,
  icon: Icon,
  withTime,
  isInfinite,
  isInfineChecked,
  registerNameCheckbox,
  watch,
  ...rest
}: DateInputProps<T>) => {
  const { t } = useTranslation();

  const variantClass = isSecondary ? 'bg-primary' : 'bg-white border-1 border-secondary-light';

  return (
    <div>
      {label && (
        <label htmlFor={registerName} className="mb-1 text-sm">
          {label}
        </label>
      )}
      <div
        className={`flex items-center pl-2 py-1 rounded-md focus-within::outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all 
				${error ? 'border-red-500' : ''}
				${isDisabled ? 'bg-gray-200' : variantClass}
				`}>
        {Icon && <Icon className="text-primary" />}
        {isInfineChecked ? (
          <span className="w-full bg-transparent focus:outline-none p-[0.55rem] h-full text-sm" id={registerName}>
            {t('common.infinitive')}
          </span>
        ) : (
          <input
            {...rest}
            type={withTime ? 'datetime-local' : 'date'}
            id={registerName}
            className="w-full h-full p-2 text-sm bg-transparent focus:outline-none"
            {...register(registerName, registerProps)}
            disabled={isDisabled || isInfineChecked}
            placeholder={placeholder}
            onFocus={(e) => {
              if (!e.target.validity.rangeUnderflow && !e.target.validity.rangeOverflow) {
                e.target.showPicker();
              }
            }}
          />
        )}
      </div>
      {isInfinite && watch && registerNameCheckbox && !isDisabled && (
        <Checkbox
          watch={watch}
          label={t('common.infinitive')}
          register={register}
          registerName={registerNameCheckbox}
          checked={isInfineChecked}
          isChecked={isInfineChecked}
        />
      )}
      {error && <span className="text-xs text-red-500">{error.message}</span>}
    </div>
  );
};
